/**
 * A heading of the navigation rail that opens onto its entries.
 *
 * A disclosure, not a menu: `role="menu"` promises arrow-key navigation and
 * a different reading mode, and a list of links under a button that says
 * whether it is expanded is what a screen-reader user expects of a sidebar.
 * The entries stay in the DOM while closed — `hidden`, so they are skipped by
 * the tab order and by assistive technology alike.
 *
 * Which group is open is the caller's state. The rail decides it from the
 * current page — open the area the reader is in — and a component that kept
 * its own would have to be told every time the route changed.
 */

import { useId, type ReactNode } from "react";

export interface NavGroupProps {
  label: ReactNode;
  /** Decorative: rendered `aria-hidden`, the label carries the meaning. */
  icon?: ReactNode;
  open: boolean;
  onToggle: () => void;
  /**
   * Whether the current page is one of this group's entries.
   *
   * Drawn on the heading, so a closed group still says "you are in here".
   * Not announced: the entry itself carries `aria-current`, and announcing it
   * twice would make the heading sound like a page.
   */
  current?: boolean;
  /** `<li>` elements. */
  children: ReactNode;
}

export function NavGroup({
  label,
  icon,
  open,
  onToggle,
  current = false,
  children,
}: NavGroupProps) {
  const listId = useId();
  return (
    <div className="hardauto-nav-group">
      <button
        type="button"
        className="hardauto-nav__link hardauto-nav-group__toggle"
        aria-expanded={open}
        aria-controls={listId}
        data-current={current ? "true" : undefined}
        onClick={onToggle}
      >
        {icon ? (
          <span className="hardauto-nav__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="hardauto-nav__label">{label}</span>
        <svg
          className="hardauto-nav-group__chevron"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      <ul id={listId} className="hardauto-nav hardauto-nav-group__items" hidden={!open}>
        {children}
      </ul>
    </div>
  );
}
